import { Icon, Intent, Tab, Tabs } from "@blueprintjs/core"
import CustomDialog from "components/Atoms/CustomDialog"
import TablePagination from "components/Molecules/TablePagination"
import ImageStack from "components/Organisms/ImageStack"

import CustomReactTable from "components/Templates/CustomReactTable"
import ModuleHeader from "components/Templates/ModuleHeader"
import { TRANSACTION_STATUS } from "data/static.items"
import { TransactionStatus, useTransactionsLazyQuery } from "generated/graphql"
import React, { useEffect, useState } from "react"
import { useSearchParams } from "react-router-dom"
import { inr } from "utils/formatters.utils"

const VendorTransactionHistory = () => {
  const PAGE_SIZE = 5
  const [params, setParams] = useSearchParams()
  const account = parseInt(params.get("transaction_history"))
  const [currentTab, setCurrentTab] = useState("all")
  const [page, setPage] = useState(0)
  const [query, setQuery] = useState("")

  const filter =
    currentTab !== "all"
      ? {
          status: [
            TransactionStatus.FAL,
            TransactionStatus.RCF,
            TransactionStatus.REF,
          ],
          accountId: account.toString(),
        }
      : {
          accountId: account.toString(),
        }

  const [getData, { data: transactionData }] = useTransactionsLazyQuery({
    variables: {
      first: PAGE_SIZE,
      skip: page * PAGE_SIZE,
      query: query,
      filter: filter,
    },
  })

  let refundTotal = 0
  transactionData?.allAccountTransactions
    ?.filter(p => {
      return (
        p.status === TransactionStatus.FAL || p.status === TransactionStatus.RCF
      )
    })
    .map(transaction => {
      return (refundTotal += transaction.amount)
    })

  useEffect(() => {
    getData()
  }, [currentTab, getData, page])

  const account_holder =
    transactionData?.allAccountTransactions[0]?.account?.name
  const account_balance =
    transactionData?.allAccountTransactions[0]?.account?.balance
  const account_codename =
    transactionData?.allAccountTransactions[0]?.account?.codename

  const getIntent = status => {
    if (status === TransactionStatus.FAL || status === TransactionStatus.RCF)
      return Intent.DANGER
    return Intent.SUCCESS
  }

  const getRefundStatus = (status: TransactionStatus) => {
    if (status === TransactionStatus.FAL || status === TransactionStatus.RCF)
      return "Not Yet"
    return "Refunded"
  }

  const COLUMNS = [
    {
      Header: currentTab === "all" ? "Status" : "Refund Status",
      accessor: "statusObj",
      width: 130,
      Cell: ({ original }) => {
        const statusObj = TRANSACTION_STATUS.find(
          e => e.id === original?.status
        )
        return (
          <>
            <Icon icon="dot" intent={getIntent(original?.status)} />
            {currentTab === "all"
              ? statusObj.name
              : getRefundStatus(statusObj.id)}
          </>
        )
      },
    },
    {
      Header: "Amount",
      className: "px-3 text-left",
      accessor: "Amount",
      width: 100,
      Cell: ({ original }) => {
        return <div className="text-center">{inr(original?.amount)}</div>
      },
    },
    {
      Header: "Transaction Type",
      accessor: "transactionType",
      width: 220,
      Cell({ original }) {
        return <div>{original?.transactionType?.name}</div>
      },
    },
    {
      width: 150,
      Header: "Payment Method",
      accessor: "paymentMethod",
      Cell({ original }) {
        return <div>{original?.paymentMethod?.name}</div>
      },
    },
    {
      Header: "Reference Id",
      accessor: "refId",
      width: 280,
      style: { whiteSpace: "unset" },
      Cell({ original }) {
        return (
          <div className="break-words flex-wrap overflo">
            {original?.referenceId}
          </div>
        )
      },
    },
    {
      width: 140,
      Header: "Remark",
      accessor: "remark",
      Cell({ original }) {
        return <>{original?.remark}</>
      },
    },
    {
      width: 100,
      Header: "Photos",
      accessor: "photos",
      Cell({ original }) {
        return <ImageStack photos={original.attachments} />
      },
    },
  ]

  return (
    <CustomDialog
      width={"80%"}
      scrollable={false}
      isOpen={account ? true : false}
      onClose={() => {
        setPage(0)
        params.delete("transaction_history")
        setParams(params)
      }}
      light
      icon="bank-account"
      //   icon="th-list"
      title={
        <span>
          <span className="text-gray-700">Account</span>{" "}
          {account_codename || "na"} |{" "}
          <span className="text-gray-700">Vendor</span>{" "}
          <span>{account_holder || "na"}</span>
        </span>
      }
    >
      <Tabs
        className="w-full"
        selectedTabId={currentTab}
        onChange={tab => {
          setCurrentTab(tab.toString())
          setPage(0)
        }}
      >
        <Tab
          id="all"
          panel={
            <>
              <ModuleHeader
                className="vt-vendor-module-header"
                query={query}
                onQueryChange={query => {
                  setQuery(query)
                  setPage(0)
                }}
                header={
                  <span>
                    Current Balance : <b>{inr(account_balance)}</b>
                  </span>
                }
                children={
                  <TablePagination
                    className="mr-3"
                    page={page}
                    onPageChange={page => {
                      setPage(page)
                    }}
                  />
                }
              ></ModuleHeader>
              <CustomReactTable
                className="!h-80"
                pageSize={PAGE_SIZE}
                data={transactionData?.allAccountTransactions ?? []}
                loadingText="Fetching Vendors.."
                columns={COLUMNS}
              />
            </>
          }
          title="All Transactions"
        ></Tab>
        <Tab
          id="cancelled"
          panel={
            <>
              <ModuleHeader
                className="vt-vendor-module-header"
                query={query}
                onQueryChange={query => {
                  setQuery(query)
                }}
                header={
                  <span>
                    Refund Amount Pending : <b>{inr(refundTotal)}</b>
                  </span>
                }
                children={
                  <TablePagination
                    className="mr-3"
                    page={page}
                    onPageChange={page => {
                      setPage(page)
                    }}
                  />
                }
              ></ModuleHeader>
              <CustomReactTable
                className="!h-80"
                pageSize={PAGE_SIZE}
                data={transactionData?.allAccountTransactions ?? []}
                loadingText="Fetching Vendors.."
                columns={COLUMNS}
              />
            </>
          }
          title="Cancelled Transactions"
        ></Tab>
      </Tabs>
    </CustomDialog>
  )
}

export default VendorTransactionHistory
